/**
 * Замечания с «Мой день» без связи (МП-02, ПР-07): черновик на телефоне,
 * уходит сам при появлении связи (тело + фото «до»).
 */
import {
  REMARK_PHOTO_EXTENSIONS,
  REMARK_PHOTO_MAX_BYTES,
  RemarkCreateAck,
  RemarkInput,
  RemarkPhotoAck,
  RemarkPhotoCommand,
} from '@epc/contracts';
import { z } from 'zod';
import { ApiError, api } from '../../../lib/api';

export type PendingRemarkPhoto = {
  id: string;
  name: string;
  type: string;
  blob: Blob;
  /** Независимый ключ команды; local id не переименовывается при migration. */
  photoOperationId?: string;
};

export function remarkPhotoError(photo: Pick<PendingRemarkPhoto, 'name' | 'blob'>): string | null {
  const label = photo.name || 'Без имени';
  if (!photo.blob.size) return `Фото «${label}» пустое. Выберите другой файл.`;
  if (photo.blob.size > REMARK_PHOTO_MAX_BYTES)
    return `Фото «${label}» больше 10 МиБ. Сохраните оригинал и подготовьте файл меньшего размера.`;
  const extension = photo.name.split('.').at(-1)?.toLowerCase();
  if (!photo.name.includes('.') || !REMARK_PHOTO_EXTENSIONS.some((allowed) => allowed === extension))
    return `Фото «${label}»: подходят только JPEG, PNG, WebP и HEIC. Сохраните оригинал и выберите поддерживаемый файл.`;
  return null;
}

function freshPhotoKey(reserved: Set<string>): string {
  for (let attempt = 0; attempt < 5; attempt++) {
    const id = crypto.randomUUID();
    if (!reserved.has(id.toLowerCase())) {
      reserved.add(id.toLowerCase());
      return id;
    }
  }
  throw new Error('Не удалось выделить отдельный ключ фото. Данные сохранены.');
}

export type PendingRemark = {
  id: string;
  scope: string;
  projectId: string;
  body: RemarkInput;
  photos: PendingRemarkPhoto[];
  createdAt: number;
  state: 'pending' | 'sending' | 'sent' | 'error';
  error: string | null;
  serverRemarkId: string | null;
  /** Отдельный durable ключ: primary ID старых записей не переименовывается. */
  clientOperationId?: string;
  createReceiptId?: string;
  serverRemarkNumber?: string;
};

export type RemarkScope = { tenantId: string; userId: string };

const scopeKey = (s: RemarkScope) => JSON.stringify([s.tenantId, s.userId]);

export class PendingRemarksStore {
  constructor(private readonly db: IDBDatabase) {}

  static open(
    factory: IDBFactory = indexedDB,
    name = 'epc-site-remarks-offline',
  ): Promise<PendingRemarksStore> {
    return new Promise((resolve, reject) => {
      const request = factory.open(name, 1);
      let blocked = false;
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains('remarks')) {
          const store = request.result.createObjectStore('remarks', { keyPath: 'id' });
          store.createIndex('scope', 'scope');
        }
      };
      request.onerror = () => reject(request.error);
      request.onblocked = () => {
        blocked = true;
        reject(new Error('Закройте другую вкладку для обновления хранилища замечаний'));
      };
      request.onsuccess = () => {
        if (blocked) {
          request.result.close();
          return;
        }
        request.result.onversionchange = () => request.result.close();
        resolve(new PendingRemarksStore(request.result));
      };
    });
  }

  get name() {
    return this.db.name;
  }

  close() {
    this.db.close();
  }

  list(scope: RemarkScope): Promise<PendingRemark[]> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction('remarks', 'readonly');
      const req = tx.objectStore('remarks').index('scope').getAll(scopeKey(scope));
      tx.oncomplete = () => {
        const rows = (req.result as PendingRemark[]).filter((r) => r.state !== 'sent');
        rows.sort((a, b) => a.createdAt - b.createdAt);
        resolve(rows);
      };
      tx.onabort = () => reject(tx.error ?? new Error('Не удалось прочитать замечания на телефоне'));
      tx.onerror = () => {};
    });
  }

  put(row: PendingRemark): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction('remarks', 'readwrite');
      tx.objectStore('remarks').put(row);
      tx.oncomplete = () => resolve();
      tx.onabort = () => reject(tx.error ?? new Error('Не удалось сохранить замечание на телефоне'));
      tx.onerror = () => {};
    });
  }

  /** Повтор enqueue не может затереть уже полученный ACK или прогресс фото. */
  insert(row: PendingRemark): Promise<PendingRemark> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction('remarks', 'readwrite');
      const records = tx.objectStore('remarks');
      const request = records.get(row.id);
      let saved = row;
      let failure: unknown;
      request.onsuccess = () => {
        try {
          const existing = request.result as PendingRemark | undefined;
          if (existing) {
            if (
              existing.scope !== row.scope ||
              existing.clientOperationId !== row.clientOperationId ||
              JSON.stringify(RemarkInput.parse(existing.body)) !== JSON.stringify(row.body)
            )
              throw new Error('Этот ключ уже относится к другому замечанию. Данные сохранены.');
            saved = existing;
          } else records.add(row);
        } catch (error) {
          failure = error;
          tx.abort();
        }
      };
      tx.oncomplete = () => resolve(saved);
      tx.onabort = () =>
        reject(failure ?? tx.error ?? new Error('Не удалось сохранить замечание на телефоне'));
      tx.onerror = () => {};
    });
  }

  remove(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction('remarks', 'readwrite');
      tx.objectStore('remarks').delete(id);
      tx.oncomplete = () => resolve();
      tx.onabort = () => reject(tx.error ?? new Error('Не удалось завершить отправку замечания'));
      tx.onerror = () => {};
    });
  }
}

export async function enqueuePendingRemark(
  store: PendingRemarksStore,
  scope: RemarkScope,
  body: RemarkInput,
  photos: PendingRemarkPhoto[],
  now = Date.now(),
  clientOperationId: string = crypto.randomUUID(),
): Promise<PendingRemark> {
  z.uuid().parse(clientOperationId);
  const reserved = new Set([clientOperationId.toLowerCase()]);
  const keyedPhotos = photos.map((photo) => {
    const error = remarkPhotoError(photo);
    if (error) throw new Error(error);
    if (photo.photoOperationId !== undefined) {
      if (
        !z.uuid().safeParse(photo.photoOperationId).success ||
        reserved.has(photo.photoOperationId.toLowerCase())
      )
        throw new Error('Ключ фото повреждён или уже используется. Оригинал сохранён.');
      reserved.add(photo.photoOperationId.toLowerCase());
      return photo;
    }
    return { ...photo, photoOperationId: freshPhotoKey(reserved) };
  });
  const row: PendingRemark = {
    id: clientOperationId,
    clientOperationId,
    scope: scopeKey(scope),
    projectId: body.projectId,
    body: RemarkInput.parse(body),
    photos: keyedPhotos,
    createdAt: now,
    state: 'pending',
    error: null,
    serverRemarkId: null,
  };
  return store.insert(row);
}

export type RemarkSyncResult = { sent: number; failed: number; left: number };

const activeSyncs = new Map<string, Promise<RemarkSyncResult>>();
const UNSUPPORTED_SYNC = 'Обновите браузер для безопасной отправки замечаний. Фото сохранены на телефоне.';

/** Одна отправка на БД и владельца, в том числе из разных вкладок. */
export function syncPendingRemarks(
  store: PendingRemarksStore,
  scope: RemarkScope,
  isCurrentIdentity: () => boolean = () => true,
): Promise<RemarkSyncResult> {
  const key = JSON.stringify(['epc-site-remarks-sync', store.name, scopeKey(scope)]);
  const active = activeSyncs.get(key);
  if (active) return active;
  const run = async () => {
    const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
    if (!locks) {
      const queue = await store.list(scope);
      for (const row of queue) await store.put({ ...row, state: 'error', error: UNSUPPORTED_SYNC });
      return { sent: 0, failed: queue.length, left: queue.length };
    }
    // Очередь читается внутри lock: другая вкладка могла уже отправить её.
    return locks.request(key, () => sendPendingRemarks(store, scope, isCurrentIdentity));
  };
  const pending = run().finally(() => activeSyncs.delete(key));
  activeSyncs.set(key, pending);
  return pending;
}

/** Серверный ID и каждое подтверждённое фото сохраняются до следующего запроса. */
async function sendPendingRemarks(
  store: PendingRemarksStore,
  scope: RemarkScope,
  isCurrentIdentity: () => boolean,
): Promise<RemarkSyncResult> {
  const assertIdentity = () => {
    if (!isCurrentIdentity())
      throw new ApiError(
        409,
        'session_context_changed',
        'Пользователь или организация изменились. Данные оставлены прежнему владельцу.',
      );
  };
  const queue = await store.list(scope);
  // Receipt namespace общий для create/photo: известные коллизии нельзя лечить новым ключом после попытки.
  const createKeys = new Map<string, string[]>();
  const photoKeys = new Map<string, string[]>();
  const blocked = new Map<string, string>();
  const keyError =
    'Ключ фото повреждён или уже используется. Оригинал сохранён; повтор с новым ключом не выполняется.';
  for (const row of queue) {
    const createKey = row.clientOperationId ?? row.id;
    if (z.uuid().safeParse(createKey).success) {
      const key = createKey.toLowerCase();
      createKeys.set(key, [...(createKeys.get(key) ?? []), row.id]);
    }
    for (const photo of row.photos) {
      if (photo.photoOperationId === undefined) continue;
      if (!z.uuid().safeParse(photo.photoOperationId).success) {
        blocked.set(row.id, keyError);
        continue;
      }
      const key = photo.photoOperationId.toLowerCase();
      photoKeys.set(key, [...(photoKeys.get(key) ?? []), row.id]);
    }
  }
  for (const [key, rows] of photoKeys) {
    if (rows.length > 1 || createKeys.has(key))
      for (const rowId of [...rows, ...(createKeys.get(key) ?? [])]) blocked.set(rowId, keyError);
  }
  const reserved = new Set([...createKeys.keys(), ...photoKeys.keys()]);
  let sent = 0;
  let failed = 0;
  for (const row of queue) {
    let progress = { ...row, state: 'sending' as PendingRemark['state'], error: null as string | null };
    try {
      if (blocked.has(row.id)) throw new Error(blocked.get(row.id));
      for (const photo of progress.photos) {
        const error = remarkPhotoError(photo);
        if (error) throw new Error(error);
      }
      progress.photos = progress.photos.map((photo) =>
        photo.photoOperationId === undefined
          ? { ...photo, photoOperationId: freshPhotoKey(reserved) }
          : photo,
      );
      if (!progress.serverRemarkId) {
        if (progress.clientOperationId === undefined) {
          progress.clientOperationId = z.uuid().safeParse(progress.id).success
            ? progress.id
            : freshPhotoKey(reserved);
        }
        if (!z.uuid().safeParse(progress.clientOperationId).success)
          throw new Error('Не удалось распознать сохранённый ключ замечания. Данные оставлены на телефоне.');
      }
      // Включая миграцию старого UUID: commit ключа обязателен ДО первого HTTP.
      await store.put(progress);
      if (!progress.serverRemarkId) {
        assertIdentity();
        const created = await api<unknown>('/supervision/remark-commands', {
          method: 'POST',
          body: {
            clientOperationId: progress.clientOperationId,
            expectedRevision: 0,
            payload: progress.body,
          },
          idempotencyKey: progress.clientOperationId,
          expectedIdentity: scope,
        });
        const ack = RemarkCreateAck.safeParse(created);
        if (!ack.success) throw new Error('Портал не подтвердил создание замечания');
        progress = {
          ...progress,
          serverRemarkId: ack.data.result.id,
          createReceiptId: ack.data.receiptId,
          serverRemarkNumber: ack.data.result.number,
        };
        await store.put(progress);
      }
      while (progress.photos.length) {
        assertIdentity();
        const photo = progress.photos[0]!;
        const command = RemarkPhotoCommand.parse({
          schemaVersion: 1,
          clientOperationId: photo.photoOperationId,
          expectedRevision: 0,
          projectId: progress.projectId,
          stage: 'before',
        });
        const form = new FormData();
        form.append('command', JSON.stringify(command));
        form.append('file', photo.blob, photo.name);
        const uploaded = await api<unknown>(
          `/supervision/remarks/${progress.serverRemarkId}/photo-commands`,
          {
            method: 'POST',
            form,
            idempotencyKey: photo.photoOperationId,
            expectedIdentity: scope,
          },
        );
        const photoAck = RemarkPhotoAck.safeParse(uploaded);
        if (!photoAck.success || photoAck.data.result.remarkId !== progress.serverRemarkId)
          throw new Error('Портал не подтвердил прикрепление фото к этому замечанию');
        // Удаляется только текущая подтверждённая операция, даже если старые local id совпадали.
        progress = { ...progress, photos: progress.photos.slice(1) };
        await store.put(progress);
      }
      await store.remove(row.id);
      sent += 1;
    } catch (e) {
      const offline = isOfflineApiError(e);
      const message = e instanceof Error ? e.message : 'Не удалось отправить';
      await store.put({
        ...progress,
        state: offline ? 'pending' : 'error',
        error: message,
      });
      if (offline) break;
      failed += 1;
      if (!isCurrentIdentity()) break;
    }
  }
  const left = (await store.list(scope)).length;
  return { sent, failed, left };
}

/** Ошибка сети / нет связи — можно сохранить на телефон. */
export function isOfflineApiError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return error.status === 0 || error.status === 429 || error.status >= 500;
}
